import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks'
import { fetchReportDetail, pollReportStatus } from '../slices/reportsSlice'
import SidebarNavigation from '../components/SidebarNavigation'
import './ReportDetailPage.css'

interface SummaryData {
  agents: string[]
  grid: Record<string, Record<string, number>>
  grid_details: Record<string, Record<string, string[]>>
  agent_totals: Record<string, { total_score: number; failure_count: number; pass_count: number; fail_count: number; dsq_count: number; disagree_count: number }>
  total_with_scores: number
  won_by_non_replay: number
  won_only_by_replay: number
  won_by_none: number
  won_by_non_replay_failures: string[]
  won_only_by_replay_failures: string[]
  won_by_none_failures: string[]
  failure_winners: Record<string, string[]>
  failure_details: Array<{ id: string; short_id: string; title: string; test_archive_url?: string; results_archive_url?: string }>
  assessments: Array<{
    failure_id: string; agent_name: string; attempt: number; status: string;
    dsq_claude_result: string; dsq_codex_result: string;
    grade_claude_result: string; grade_codex_result: string;
    root_cause_judge_result: string; time_seconds: number; cost: number;
  }>
}

function parseShortId(shortId: string): number {
  const match = shortId.match(/(\d+)$/)
  return match ? parseInt(match[1], 10) : 0
}

function formatTime(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 60) return `${Math.round(seconds)}s`
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  return `${mins}m${secs > 0 ? ` ${secs}s` : ''}`
}

function sortFailureIds(ids: string[], failureMap: Map<string, { short_id: string; title: string; test_archive_url?: string; results_archive_url?: string }>) {
  return [...ids].sort((a, b) => {
    const fa = failureMap.get(a)
    const fb = failureMap.get(b)
    return parseShortId(fa?.short_id ?? '') - parseShortId(fb?.short_id ?? '')
  })
}

function FailureLink({ id, failureMap }: { id: string; failureMap: Map<string, { short_id: string; title: string; test_archive_url?: string; results_archive_url?: string }> }) {
  const f = failureMap.get(id)
  return (
    <a href={`#failure-${id}`} className="report-failure-link">
      {f?.short_id || id.slice(0, 8)}
    </a>
  )
}

function FailureListWithWinners({ title, failureIds, failureMap, failureWinners, isReplayAgent }: {
  title: string; failureIds: string[];
  failureMap: Map<string, { short_id: string; title: string; test_archive_url?: string; results_archive_url?: string }>;
  failureWinners?: Record<string, string[]>;
  isReplayAgent: (name: string) => boolean;
}) {
  if (failureIds.length === 0) return null
  const sorted = sortFailureIds(failureIds, failureMap)
  return (
    <div className="report-failure-list">
      <h4>{title} ({failureIds.length})</h4>
      <ul>
        {sorted.map((id) => {
          const f = failureMap.get(id)
          const winners = failureWinners?.[id]?.filter(w => !isReplayAgent(w)) ?? []
          return (
            <li key={id}>
              <FailureLink id={id} failureMap={failureMap} />
              {f?.title && <span className="report-failure-title"> — {f.title}</span>}
              {winners.length > 0 && (
                <span className="report-failure-winners"> [{winners.join(', ')}]</span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function FailureList({ title, failureIds, failureMap }: { title: string; failureIds: string[]; failureMap: Map<string, { short_id: string; title: string; test_archive_url?: string; results_archive_url?: string }> }) {
  if (failureIds.length === 0) return null
  const sorted = sortFailureIds(failureIds, failureMap)
  return (
    <div className="report-failure-list">
      <h4>{title} ({failureIds.length})</h4>
      <ul>
        {sorted.map((id) => {
          const f = failureMap.get(id)
          return (
            <li key={id}>
              <FailureLink id={id} failureMap={failureMap} />
              {f?.title && <span className="report-failure-title"> — {f.title}</span>}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function GridCell({ winner, loser, count, details, failureMap }: {
  winner: string; loser: string; count: number;
  details: string[] | undefined;
  failureMap: Map<string, { short_id: string; title: string; test_archive_url?: string; results_archive_url?: string }>;
}) {
  const [expanded, setExpanded] = useState(false)
  if (winner === loser) return <td className="report-grid-cell report-grid-diagonal">—</td>
  if (count === 0) return <td className="report-grid-cell">0</td>

  const sorted = details ? sortFailureIds(details, failureMap) : []

  return (
    <td className="report-grid-cell report-grid-nonzero report-grid-expandable" onClick={() => setExpanded(!expanded)}>
      <span className="report-grid-cell-count">{count}</span>
      {expanded && sorted.length > 0 && (
        <div className="report-grid-cell-details">
          {sorted.map((id) => {
            const f = failureMap.get(id)
            return (
              <div key={id} className="report-grid-cell-failure">
                <a href={`#failure-${id}`} className="report-failure-link" onClick={(e) => e.stopPropagation()}>
                  {f?.short_id || id.slice(0, 8)}
                </a>
              </div>
            )
          })}
        </div>
      )}
    </td>
  )
}

function ReportDetailPage() {
  const { reportId } = useParams<{ reportId: string }>()
  const dispatch = useAppDispatch()
  const { currentReport, currentReportLoading, currentReportError } = useAppSelector((state) => state.reports)

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (reportId) dispatch(fetchReportDetail(reportId))
  }, [dispatch, reportId])

  // Lightweight poll for status/archive URLs only (no full page re-render)
  const reportStatus = currentReport?.status

  useEffect(() => {
    if ((reportStatus === 'pending' || reportStatus === 'generating') && reportId && !pollRef.current) {
      pollRef.current = setInterval(() => dispatch(pollReportStatus(reportId)), 5000)
    } else if (reportStatus !== 'pending' && reportStatus !== 'generating' && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null } }
  }, [reportStatus, reportId, dispatch])

  const summary = useMemo<SummaryData | null>(() => {
    if (!currentReport?.summary_data) return null
    try {
      const parsed = typeof currentReport.summary_data === 'string'
        ? JSON.parse(currentReport.summary_data)
        : currentReport.summary_data
      // Validate that parsed data has required fields (empty for pending reports)
      if (!parsed?.agents || !parsed?.failure_details) return null
      return parsed as SummaryData
    } catch { return null }
  }, [currentReport])

  // When status transitions to completed and summary data is missing, do one full fetch
  const prevStatusRef = useRef(reportStatus)
  useEffect(() => {
    if (prevStatusRef.current !== reportStatus && reportStatus === 'completed' && !summary && reportId) {
      dispatch(fetchReportDetail(reportId))
    }
    prevStatusRef.current = reportStatus
  }, [reportStatus, summary, reportId, dispatch])

  const failureMap = useMemo(() => {
    if (!summary) return new Map<string, { short_id: string; title: string; test_archive_url?: string; results_archive_url?: string }>()
    return new Map(summary.failure_details.map(f => [f.id, f]))
  }, [summary])

  const sortedFailureDetails = useMemo(() => {
    if (!summary) return []
    return [...summary.failure_details].sort((a, b) => parseShortId(a.short_id) - parseShortId(b.short_id))
  }, [summary])

  const assessmentsByFailure = useMemo(() => {
    if (!summary) return new Map<string, SummaryData["assessments"]>()
    const map = new Map<string, SummaryData["assessments"]>()
    for (const a of summary.assessments) {
      if (!map.has(a.failure_id)) map.set(a.failure_id, [])
      map.get(a.failure_id)!.push(a)
    }
    return map
  }, [summary])

  const isReplayAgent = (name: string) => name.toLowerCase().includes('replay')

  if (currentReportLoading) return (
    <div className="report-detail-page">
      <SidebarNavigation />
      <main className="app-page-main"><div className="report-loading">Loading report...</div></main>
    </div>
  )

  if (currentReportError || !currentReport) return (
    <div className="report-detail-page">
      <SidebarNavigation />
      <main className="app-page-main"><div className="report-error">{currentReportError || 'Report not found'}</div></main>
    </div>
  )

  return (
    <div data-testid="report-detail-page" className="report-detail-page">
      <SidebarNavigation />
      <main className="app-page-main">
        <header className="app-page-sticky-header">
          <div className="app-page-sticky-header-inner">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Link to="/reports" className="report-back-link">Reports</Link>
              <span style={{ color: 'var(--color-text-muted)' }}>/</span>
              <h1 className="app-page-title" style={{ margin: 0 }}>{currentReport.title}</h1>
            </div>
            <div className="report-meta">
              <span>Generated: {new Date(currentReport.created_at).toLocaleString()}</span>
              {currentReport.tag_filter && <span className="report-filter-badge">tag: {currentReport.tag_filter}</span>}
              {currentReport.search_filter && <span className="report-filter-badge">search: {currentReport.search_filter}</span>}
            </div>
          </div>
        </header>

        {/* Status / generating state */}
        {currentReport.status === 'pending' && (
          <div className="report-status-banner report-status-pending">
            Report is queued for generation...
          </div>
        )}
        {currentReport.status === 'generating' && (
          <div className="report-status-banner report-status-generating">
            Report is being generated. This may take a few minutes — building archives and uploading...
          </div>
        )}
        {currentReport.status === 'error' && (
          <div className="report-status-banner report-status-error">
            Report generation failed: {currentReport.error || 'Unknown error'}
          </div>
        )}

        {summary && (
          <div className="report-content">

            {/* ── Section 1: Agent Scores ── */}
            <section className="report-section">
              <h2>1. Agent Scores</h2>
              <p className="report-section-subtitle">
                Aggregate scores across {summary.total_with_scores} failures. Score = passes + 0.5 per disagreement. % = score / (failures x 3).
              </p>
              <div className="report-grid-wrapper">
                <table className="report-grid">
                  <thead>
                    <tr>
                      <th className="report-grid-header">Agent</th>
                      <th className="report-grid-header">Score</th>
                      <th className="report-grid-header">%</th>
                      <th className="report-grid-header">Failures</th>
                      <th className="report-grid-header">Pass</th>
                      <th className="report-grid-header">Fail</th>
                      <th className="report-grid-header">DSQ</th>
                      <th className="report-grid-header">Disagree</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.agents.map((agent) => {
                      const t = summary.agent_totals[agent]
                      if (!t) return null
                      const maxScore = t.failure_count * 3
                      const pct = maxScore > 0 ? Math.round(t.total_score / maxScore * 100) : 0
                      return (
                        <tr key={agent}>
                          <td className="report-grid-row-header">{agent}</td>
                          <td className="report-grid-cell">{t.total_score.toFixed(1)}</td>
                          <td className="report-grid-cell"><span className={pct > 0 ? 'status-pass' : ''}>{pct}%</span></td>
                          <td className="report-grid-cell">{t.failure_count}</td>
                          <td className="report-grid-cell status-pass">{t.pass_count}</td>
                          <td className="report-grid-cell status-fail">{t.fail_count}</td>
                          <td className="report-grid-cell">{t.dsq_count}</td>
                          <td className="report-grid-cell" style={{ color: 'var(--color-warning, #d4a017)' }}>{t.disagree_count}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            {/* ── Section 2: Agent vs Agent Performance ── */}
            {summary.agents.length > 1 && (
              <section className="report-section">
                <h2>2. Agent vs Agent Performance</h2>
                <p className="report-section-subtitle">
                  Columns are winners, rows are losers. An agent "wins" a failure with 2+ passes or disagreements.
                  An agent "loses" with 2+ fails. Click a cell to see affected failures.
                </p>
                <div className="report-grid-wrapper">
                  <table className="report-grid">
                    <thead>
                      <tr>
                        <th className="report-grid-corner"></th>
                        {summary.agents.map((a) => (
                          <th key={a} className="report-grid-header">{a}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {summary.agents.map((loser) => (
                        <tr key={loser}>
                          <td className="report-grid-row-header">{loser}</td>
                          {summary.agents.map((winner) => (
                            <GridCell
                              key={winner}
                              winner={winner}
                              loser={loser}
                              count={summary.grid[winner]?.[loser] ?? 0}
                              details={summary.grid_details?.[winner]?.[loser]}
                              failureMap={failureMap}
                            />
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Grid detail lists */}
                <div className="report-grid-detail-lists">
                  {summary.agents.map((winner) =>
                    summary.agents.map((loser) => {
                      if (winner === loser) return null
                      const ids = summary.grid_details?.[winner]?.[loser]
                      if (!ids || ids.length === 0) return null
                      return (
                        <FailureList
                          key={`${winner}-${loser}`}
                          title={`${winner} beat ${loser}`}
                          failureIds={ids}
                          failureMap={failureMap}
                        />
                      )
                    })
                  )}
                </div>
              </section>
            )}

            {/* ── Section 3: Win Percentages ── */}
            <section className="report-section">
              <h2>3. Win Percentages</h2>
              <p className="report-section-subtitle">
                An agent "wins" a failure if it has 2 or more passes or disagreements across its attempts.
                {' '}{summary.total_with_scores} failures evaluated.
              </p>

              <div className="report-stats">
                <div className="report-stat">
                  <span className="report-stat-value status-pass">
                    {summary.total_with_scores > 0 ? Math.round(summary.won_by_non_replay / summary.total_with_scores * 100) : 0}%
                  </span>
                  <span className="report-stat-label">Won by any non-Replay agent ({summary.won_by_non_replay})</span>
                </div>
                <div className="report-stat">
                  <span className="report-stat-value" style={{ color: 'var(--color-primary, #2563eb)' }}>
                    {summary.total_with_scores > 0 ? Math.round(summary.won_only_by_replay / summary.total_with_scores * 100) : 0}%
                  </span>
                  <span className="report-stat-label">Won only by Replay agent ({summary.won_only_by_replay})</span>
                </div>
                <div className="report-stat">
                  <span className="report-stat-value status-fail">
                    {summary.total_with_scores > 0 ? Math.round(summary.won_by_none / summary.total_with_scores * 100) : 0}%
                  </span>
                  <span className="report-stat-label">No agent won ({summary.won_by_none})</span>
                </div>
              </div>

              <FailureListWithWinners
                title="Won by non-Replay agent"
                failureIds={summary.won_by_non_replay_failures}
                failureMap={failureMap}
                failureWinners={summary.failure_winners}
                isReplayAgent={isReplayAgent}
              />
              <FailureList title="Won only by Replay agent" failureIds={summary.won_only_by_replay_failures} failureMap={failureMap} />
              <FailureList title="No agent won" failureIds={summary.won_by_none_failures} failureMap={failureMap} />
            </section>

            {/* ── Section 4: Failure Details ── */}
            <section className="report-section">
              <h2>4. Failure Details</h2>
              <p className="report-section-subtitle">
                Per-failure assessment results across all agents and attempts. {sortedFailureDetails.length} failures.
              </p>
              {sortedFailureDetails.map((failure) => {
                const assessments = assessmentsByFailure.get(failure.id)
                if (!assessments || assessments.length === 0) return null
                return (
                  <div key={failure.id} id={`failure-${failure.id}`} className="report-assessment-group">
                    <h4>
                      <Link to={`/failures/${failure.id}`} className="report-failure-link">{failure.short_id}</Link>
                      <span className="report-failure-title"> — {failure.title}</span>
                      {(failure.test_archive_url || failure.results_archive_url) && (
                        <span className="report-failure-archives">
                          {failure.test_archive_url && (
                            <a href={failure.test_archive_url} className="report-archive-link report-archive-link-small" download>Test</a>
                          )}
                          {failure.results_archive_url && (
                            <a href={failure.results_archive_url} className="report-archive-link report-archive-link-small" download>Results</a>
                          )}
                        </span>
                      )}
                    </h4>
                    <table className="report-assessment-table">
                      <thead>
                        <tr>
                          <th>Agent</th>
                          <th>Attempt</th>
                          <th>Status</th>
                          <th>DSQ (Claude)</th>
                          <th>DSQ (Codex)</th>
                          <th>Grade (Claude)</th>
                          <th>Grade (Codex)</th>
                          <th>Root Cause</th>
                          <th>Time</th>
                          <th>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assessments.map((a, i) => (
                          <tr key={i}>
                            <td>{a.agent_name}</td>
                            <td>{a.attempt}</td>
                            <td className={`status-${a.status}`}>{a.status}</td>
                            <td>{a.dsq_claude_result || '—'}</td>
                            <td>{a.dsq_codex_result || '—'}</td>
                            <td>{a.grade_claude_result || '—'}</td>
                            <td>{a.grade_codex_result || '—'}</td>
                            <td>{a.root_cause_judge_result || '—'}</td>
                            <td>{formatTime(a.time_seconds)}</td>
                            <td>${a.cost?.toFixed(2) ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              })}
            </section>
          </div>
        )}
      </main>
    </div>
  )
}

export default ReportDetailPage
